import { useMemo } from "react"
import { makeStyles } from "tss-react/mui"
import { SelectChangeEvent } from "@mui/material/Select"
import { FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, OutlinedInput } from "@mui/material"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { selectEffectiveUserType } from "../../../../modules/context/contextSelectors"
import { useSupplier } from "../../../../modules/supplier/supplierHooks"
import { useAppSelector } from "../../../../modules/store/storeHooks"
import {
    fetchAllMerchants,
    fetchMerchantsForSupplier,
    MerchantsResponse,
    SupplierMerchantsQuery,
} from "../../../../modules/supplier/reporting/merchants/merchantsApi"
import {
    CompanyReportsAccountingQuery,
    fetchCompaniesMerchants,
} from "../../../../modules/company/reports/accounting/pos/posApi"
import { UserType } from "../../../../modules/login/loginTypes"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { MenuProps } from "../constants/filterMenuConst"

const useStyles = makeStyles()(() => ({
    input: {
        width: "20rem",
    },
}))

type StandardMerchantsMultiselectFilterProps = Readonly<{
    setValue: (value: string[]) => void
    value: string[]
}>

function transformData(data: MerchantsResponse) {
    return data.merchants
}

const useFetchMerchants = (currentUserType: UserType | undefined, supplierId: number | undefined) => {
    if (currentUserType === UserType.SUPPLIER && supplierId !== undefined) {
        return useApiQuery(fetchMerchantsForSupplier, {
            queryName: SupplierMerchantsQuery.MERCHANTS_FOR_SUPPLIER,
            dependencies: { supplierId },
            select: transformData,
        })
    }
    if (currentUserType === UserType.COMPANY) {
        return useApiQuery(fetchCompaniesMerchants, {
            queryName: CompanyReportsAccountingQuery.MERCHANTS_FOR_COMPANY,
            dependencies: {
                companyId: supplierId ?? 0,
            },
            select: (data) => data.merchants,
        })
    } else {
        return useApiQuery(fetchAllMerchants, {
            queryName: SupplierMerchantsQuery.ALL_MERCHANTS,
            dependencies: {},
            select: transformData,
        })
    }
}

export function StandardMerchantsMultiselectFilter({ value, setValue }: StandardMerchantsMultiselectFilterProps) {
    const { classes } = useStyles()

    const currentUserType = useAppSelector(selectEffectiveUserType)
    const { supplierId } = useSupplier()

    const { data = [] } = useFetchMerchants(currentUserType, supplierId)

    const merchantsMap = useMemo(() => {
        const map: { [id: string]: string } = {}
        data?.forEach((merchant) => {
            map[merchant.id] = merchant.name
        })
        return map
    }, [data])

    const selectedNames = value.map((id) => merchantsMap[id] || "")

    const handleChange = (event: SelectChangeEvent<typeof selectedNames>) => {
        const {
            target: { value },
        } = event

        const selectedValues = typeof value === "string" ? value.split(",") : value
        const selectedIdValues = selectedValues
            .map((name) => Object.keys(merchantsMap).find((key) => merchantsMap[key] === name))
            .filter((id): id is string => id !== undefined)

        setValue(selectedIdValues)
    }

    return (
        <FormControl className={classes.input}>
            <LocalizedStrict id="standard-merchants-filter-label">
                <InputLabel id="multiple-checkbox-merchants-label">Merchants</InputLabel>
            </LocalizedStrict>
            <Select
                labelId="multiple-checkbox-merchants-label"
                id="multiple-checkbox-merchants"
                multiple
                value={selectedNames}
                onChange={handleChange}
                input={<OutlinedInput label="Merchants" />}
                renderValue={(selected) => selected.join(", ")}
                MenuProps={MenuProps}
            >
                {data?.map((merchant) => (
                    <MenuItem key={merchant.id} value={merchant.name}>
                        <Checkbox checked={selectedNames.indexOf(merchant.name) > -1} />
                        <ListItemText primary={merchant.name} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}
